import { type ApolloQueryResult, useMutation, useQuery } from '@apollo/client'
import {
  FetchPredictedRenderDurationDocument,
  type FetchPredictedRenderDurationQuery,
  FetchViewSyncTimeDocument,
  RecordRenderDurationDocument,
  type ViewFieldsFragment,
} from '@salescore/client-api'
import { Posthog, POSTHOG_EVENTS } from '@salescore/client-base'
import { NextTickRender, useMe } from '@salescore/client-common'
import {
  deleteDurationMetrics,
  type DurationMetrics,
  selectDurationMetrics,
  selectShouldUseRefetchCache,
  setDurationMetrics,
  useAppDispatch,
  useAppSelector,
} from '@salescore/client-redux'
import { CORE_CONSTANT } from '@salescore/core'
import platform from 'platform'
import { type ReactElement, useEffect } from 'react'

// DurationMetricsを計測するためのコンポーネント・hooks群

interface DurationTimerStartArguments {
  trigger: DurationMetrics['trigger']
  viewId: string
  organizationId: string
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
export function DurationTimerStart(arguments_: DurationTimerStartArguments) {
  return (
    <NextTickRender>
      <TimerStartBody {...arguments_} />
    </NextTickRender>
  )
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
export function DurationTimerEnd({ view }: { view: ViewFieldsFragment }) {
  return (
    <NextTickRender>
      <TimerEndBody view={view} />
    </NextTickRender>
  )
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function TimerStartBody({ trigger, viewId, organizationId }: DurationTimerStartArguments) {
  const { data: oldestSyncedAtData } = useQuery(FetchViewSyncTimeDocument, {
    variables: {
      organizationId,
      viewId,
    },
  })
  const oldestSyncedAt = oldestSyncedAtData?.viewOldestSyncTime ?? undefined
  const shouldUseRefetchCache = useAppSelector((state) =>
    selectShouldUseRefetchCache(state, {
      viewId,
      oldestSyncedAt,
    }),
  )

  const { refetch } = useQuery(FetchPredictedRenderDurationDocument, {
    variables: { organizationId, viewId },
  })
  const dispatch = useAppDispatch()
  // 計測開始時にdurationMetricsに新規のmetricsを追加し
  // 平均duration取得のクエリを発行する
  useEffect(() => {
    // drillDownのシートではviewIdにprefixがついた状態でdurationが記録されているので、
    // 新規に計測を開始する時にもMetricsにあらかじめprefixをつけておく
    const metricsViewId =
      trigger === 'drillDownModalOpened' ? CORE_CONSTANT.KPI_SHEET_DYNAMIC_VIEW_ID_PREFIX + viewId : viewId
    const startAt = performance.now()

    void fetchAndSetPredictedDuration({
      refetch,
      setDuration: (cachedPredictedDuration: number | undefined, noCachedPredictedDuration: number | undefined) => {
        dispatch(
          setDurationMetrics({
            viewId: metricsViewId,
            startAt,
            trigger,
            isRefetchCacheUsed: shouldUseRefetchCache,
            cachedPredictedDuration,
            noCachedPredictedDuration,
          }),
        )
      },
    })
  }, [viewId, trigger])

  return <></>
}

const TimerEndBody = ({ view }: { view: ViewFieldsFragment }): ReactElement => {
  const { myUser, organization } = useMe()
  const targetMetrics = useAppSelector((state) => selectDurationMetrics(state, view.id))
  const dispatch = useAppDispatch()
  const [recordRenderDurationMutation] = useMutation(RecordRenderDurationDocument)

  const logBaseInfo = {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    email: myUser.identity.email || 'unknown@buffup.jp',
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    organizationId: myUser.organization.id || 'Unknown',
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    organizationName: myUser.organization.name || 'Unknown',
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    userId: myUser.id || 'Unknown',
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    userName: myUser.name || 'Unknown',
    viewId: view.id,
    viewName: view.name,
    viewType: view.config.type,
    platform: platform.toString(),
  }

  // 計測が終了したら、計測結果をmutationで送信して
  // 該当のmetricsを削除する
  useEffect(() => {
    if (targetMetrics === undefined) {
      return
    }

    const { startAt, isRefetchCacheUsed, viewId, trigger } = targetMetrics
    const endAt = performance.now()
    const viewType = view.config.type

    const duration = Math.floor(endAt - startAt)
    void recordRenderDurationMutation({
      variables: {
        organizationId: organization.id,
        renderDuration: {
          viewId,
          viewType,
          trigger,
          duration,
          isRecordsCached: isRefetchCacheUsed,
        },
      },
    })

    Posthog.track(POSTHOG_EVENTS.render_duration, {
      ...logBaseInfo,
      duration,
      trigger,
      isRecordsCached: isRefetchCacheUsed,
    })
    dispatch(deleteDurationMetrics(viewId))
  }, [])

  return <></>
}

async function fetchAndSetPredictedDuration({
  refetch,
  setDuration,
}: {
  refetch: () => Promise<ApolloQueryResult<FetchPredictedRenderDurationQuery>>
  setDuration: (cachedPredictedDuration: number | undefined, noCachedPredictedDuration: number | undefined) => void
}): Promise<void> {
  const {
    data: { predictedRenderDuration },
  } = await refetch()

  const cachedDuration = predictedRenderDuration.find((x) => x.isRecordsCached === true)
  const noCachedDuration = predictedRenderDuration.find((x) => x.isRecordsCached !== true)

  setDuration(cachedDuration?.predictedDuration ?? undefined, noCachedDuration?.predictedDuration ?? undefined)
}
